import type { SponsorDto, SponsorFurtherRequests } from '@ukinfo/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { ApiError, api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { SPONSOR_STATUS_LABELS } from '../../lib/format';
import { PersonSelect } from '../PersonPicker';
import { Button, Checkbox, Dialog, Field, Input, Select, Textarea, useToast } from '../ui';
import { FURTHER_REQUEST_ICONS, FURTHER_REQUEST_LABELS, useSponsorCategories, useSponsoringOffers } from './common';

/**
 * Sponsor anlegen oder bearbeiten.
 *
 * Die Grundinformation pflegt, wer Sponsoren bearbeiten darf. Notizen und
 * externe Ansprechpartner gehoeren zu den vertraulichen Vertragsdaten.
 */
export function SponsorDialog({
  sponsor,
  onClose,
  onSaved,
}: {
  sponsor: SponsorDto | null;
  onClose: () => void;
  onSaved?: (id: string) => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { can } = useAuth();
  const mayContracts = can('sponsoring_contracts', 'update');
  const isEdit = sponsor !== null;
  const categories = useSponsorCategories();
  const offers = useSponsoringOffers();

  const [companyName, setCompanyName] = useState(sponsor?.companyName ?? '');
  const [website, setWebsite] = useState(sponsor?.website ?? '');
  const [status, setStatus] = useState<string>(sponsor?.status ?? 'PROSPECT');
  const [categoryId, setCategoryId] = useState(sponsor?.categoryId ?? '');
  const [managedBy, setManagedBy] = useState<string | null>(sponsor?.managedByPersonId ?? null);
  const [furtherRequests, setFurtherRequests] = useState<SponsorFurtherRequests>(
    sponsor?.furtherRequests ?? 'VIA_MANAGER',
  );
  const [interestIds, setInterestIds] = useState<string[]>(sponsor?.interestOffers.map((o) => o.id) ?? []);
  const [openFor, setOpenFor] = useState(sponsor?.openFor ?? '');
  const [notes, setNotes] = useState(sponsor?.notes ?? '');
  const [contactFirst, setContactFirst] = useState('');
  const [contactLast, setContactLast] = useState('');
  const [contactEmail, setContactEmail] = useState('');

  // Eine archivierte Kategorie bleibt am Sponsor waehlbar, bis sie jemand aendert.
  const categoryOptions = [
    ...(categories.data ?? []),
    ...(sponsor?.categoryId && !categories.data?.some((c) => c.id === sponsor.categoryId)
      ? [{ id: sponsor.categoryId, name: sponsor.categoryName ?? 'Archivierte Kategorie' }]
      : []),
  ];
  const offerOptions = [
    ...(offers.data ?? []),
    ...(sponsor?.interestOffers.filter((o) => !offers.data?.some((x) => x.id === o.id)) ?? []),
  ];

  const save = useMutation({
    mutationFn: async (): Promise<string> => {
      const payload: Record<string, unknown> = {
        companyName,
        website: website || null,
        status,
        categoryId: categoryId || null,
        managedByPersonId: managedBy,
        furtherRequests,
        interestOfferIds: interestIds,
        openFor: openFor || null,
      };
      if (mayContracts) payload['notes'] = notes || null;

      if (isEdit) {
        await api.patch(`/api/sponsors/${sponsor.id}`, payload);
        return sponsor.id;
      }
      const created = await api.post<{ id: string }>('/api/sponsors', payload);
      if (mayContracts && contactFirst && contactLast) {
        await api.post(`/api/sponsors/${created.id}/contacts`, {
          firstName: contactFirst,
          lastName: contactLast,
          email: contactEmail || null,
        });
      }
      return created.id;
    },
    onSuccess: async (id) => {
      await queryClient.invalidateQueries({ queryKey: ['sponsors'] });
      await queryClient.invalidateQueries({ queryKey: ['sponsoring-offers'] });
      await queryClient.invalidateQueries({ queryKey: ['sponsor-categories'] });
      toast.show(isEdit ? 'Sponsor gespeichert' : 'Sponsor angelegt');
      onSaved?.(id);
      onClose();
    },
    onError: (err: unknown) =>
      toast.show(err instanceof ApiError ? err.message : 'Speichern fehlgeschlagen', 'error'),
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title={isEdit ? `${sponsor.companyName} bearbeiten` : 'Neuer Sponsor'}
      wide
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Abbrechen</Button>
          <Button onClick={() => save.mutate()} loading={save.isPending} disabled={!companyName.trim()}>
            Speichern
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Firmenname" required>
          {(id) => <Input id={id} value={companyName} onChange={(e) => setCompanyName(e.target.value)} />}
        </Field>
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Website">
            {(id) => (
              <Input id={id} value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="https://…" />
            )}
          </Field>
          <Field label="Status">
            {(id) => (
              <Select id={id} value={status} onChange={(e) => setStatus(e.target.value)}>
                {Object.entries(SPONSOR_STATUS_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </Select>
            )}
          </Field>
          <Field label="Kategorie" hint="Freiwillig">
            {(id) => (
              <Select id={id} value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
                <option value="">– ohne Kategorie –</option>
                {categoryOptions.map((category) => (
                  <option key={category.id} value={category.id}>{category.name}</option>
                ))}
              </Select>
            )}
          </Field>
        </div>

        <div className="space-y-3 rounded-lg bg-slate-50 p-3">
          <p className="text-sm font-medium text-slate-700">Für alle sichtbar</p>
          <p className="text-xs text-slate-500">
            Damit niemand doppelt anfragt: wer den Sponsor betreut, ob weitere Anfragen möglich sind und wofür.
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Betreut von">
              {(id) => <PersonSelect id={id} value={managedBy} onChange={setManagedBy} />}
            </Field>
            <Field label="Weitere Anfragen">
              {(id) => (
                <Select
                  id={id}
                  value={furtherRequests}
                  onChange={(e) => setFurtherRequests(e.target.value as SponsorFurtherRequests)}
                >
                  {(Object.keys(FURTHER_REQUEST_LABELS) as SponsorFurtherRequests[]).map((value) => (
                    <option key={value} value={value}>
                      {FURTHER_REQUEST_ICONS[value]} {FURTHER_REQUEST_LABELS[value]}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          </div>
          {offerOptions.length > 0 && (
            <Field label="Ansprechbar für" hint="Werbemöglichkeiten, für die der Sponsor grundsätzlich in Frage kommt">
              {() => (
                <div className="grid gap-1 rounded-lg border border-slate-300 bg-white p-2 sm:grid-cols-2">
                  {offerOptions.map((offer) => (
                    <Checkbox
                      key={offer.id}
                      label={offer.title}
                      checked={interestIds.includes(offer.id)}
                      onChange={(checked) =>
                        setInterestIds(checked ? [...interestIds, offer.id] : interestIds.filter((i) => i !== offer.id))
                      }
                    />
                  ))}
                </div>
              )}
            </Field>
          )}
          <Field label="Weitere Hinweise" hint="z. B. „nur Nachwuchs“, „nicht vor Saisonstart“">
            {(id) => <Input id={id} value={openFor} onChange={(e) => setOpenFor(e.target.value)} />}
          </Field>
        </div>

        {mayContracts && !isEdit && (
          <div className="rounded-lg bg-slate-50 p-3">
            <p className="mb-2 text-sm font-medium text-slate-700">🔒 Ansprechpartner beim Sponsor (optional)</p>
            <div className="grid gap-3 sm:grid-cols-3">
              <Field label="Vorname">
                {(id) => <Input id={id} value={contactFirst} onChange={(e) => setContactFirst(e.target.value)} />}
              </Field>
              <Field label="Nachname">
                {(id) => <Input id={id} value={contactLast} onChange={(e) => setContactLast(e.target.value)} />}
              </Field>
              <Field label="E-Mail">
                {(id) => (
                  <Input id={id} type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} />
                )}
              </Field>
            </div>
          </div>
        )}
        {mayContracts && (
          <Field label="🔒 Notizen" hint="Nur für Personen mit Zugriff auf Sponsoringverträge sichtbar">
            {(id) => <Textarea id={id} rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />}
          </Field>
        )}
      </div>
    </Dialog>
  );
}
